import styled, { css } from 'styled-components';
import themeGet from "@styled-system/theme-get";
import { AccordionItem, AccordionItemButton, AccordionItemPanel } from "react-accessible-accordion";
import { readableColor } from 'polished';
import { GatsbyImage } from 'gatsby-plugin-image';

import ChevronDown from '../../images/homepage-refresh/red-triangle-icon-down.svg';
import greySearch from '../../images/search-icon_grey.svg';
import { fixPropWarnings } from '../../utils/fixPropWarnings';
import { PredictiveSearchContainer } from '../SiteSearch/GlobalSearch';

export const FAQsListItem = styled(AccordionItem)`
    text-align: left;
    position: relative;
    margin: 0;
    border-bottom: 1px solid ${themeGet('colors.border-grey')};
    align-items: center;
`;
const ButtonIconCss = css`
    content: url(${ChevronDown});
    display: block;
    position: absolute;
    height: 8px;
    width: 13px;
    right: 16px;
    top: 18px;
`;
const openButtonCss = css`
    transform: rotate(-180deg);
    ${ButtonIconCss}
`;

const collapsedButtonCss = css`
    ${ButtonIconCss}
`;

export const Question = styled(AccordionItemButton).withConfig(fixPropWarnings('expanded'))<{ expanded?: string }>`
  background-color: ${({ expanded }) => (expanded === 'true' && themeGet('colors.light-grey'))};
  cursor: pointer;
  font-weight: ${({ expanded }) => (expanded === 'true' ? '600' : '400')};
  font-size: 18px;
  line-height: 24px;
  width: 100%;
  color: ${({ expanded }) => (expanded === 'true' ? themeGet('colors.red') : '#2b2c32')};
  padding: 10px 32px 10px 10px;

  &:after {
    ${({ expanded }) => expanded === 'true' ? openButtonCss : collapsedButtonCss}
  }

  span {
    text-decoration: none;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: 18px;
  }
`;

export const Answer = styled(AccordionItemPanel)`
  padding: 10px;
  background-color: ${themeGet('colors.light-grey')};
  b {
      font-weight: 600;
  }
  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      b {
          font-weight: 500;
      }
  }
  font-size: 16px;
  ul {
    margin-left: 20px;
    li {
      list-style-image: none;
      list-style-type: disc;
    }
  }
`;

export const Section = styled.section<{ background: string }>`
  background-color: ${({ background }) => themeGet(`colors.${background}`)};
  color: ${({ theme, background }) => readableColor(
    theme.colors[background],
    theme.colors.black,
    theme.colors.white,
  )};
  padding: 40px 0;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding: 60px 0;
  }
`;

export const Heading = styled.h1`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading1.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading1.fontSize')};
  line-height: ${themeGet('typography.mobile.heading1.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading1.letterSpacing')};
  text-align: center;
  margin: 30px 20px 20px 20px;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading1.fontSize')};
    line-height: ${themeGet('typography.desktop.heading1.lineHeight')};
  }
`;

export const Content = styled.div`
  margin: auto;
  text-align: center;
`;

export const BodyCopy = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body2.fontWeight')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    font-size: ${themeGet('typography.desktop.body2.fontSize')};
    line-height: ${themeGet('typography.desktop.body2.lineHeight')};
  }
`;

export const Image = styled(GatsbyImage)<{ size: string }>`
  width: 100%;
  max-width: 375px;
  margin: 0 auto 10px;
  object-fit: contain;

  ${({ size }) => size === 'large' && css`
    max-width: none;
  `}

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    margin: 0;
    max-width: 560px;
    border-radius: 12px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    margin: 0 40px 0 0;
  }
`;

export const Search = styled.input`
  color: black;
  padding: 3px 20px 0px 50px;
  border-radius: 30px;
  box-shadow: 0 2px 12px 0 rgba(180, 183, 189, 0.12);
  max-width: 600px;
  width: 100%;
  transition: all 0.5s ease-in-out;
  height: 42px;
  background: ${themeGet('colors.white')} url(${greySearch}) no-repeat 18px
    center;
  box-shadow: 0 2px 4px 0 rgba(47, 47, 47, 0.03);
  border-radius: 30px;
  line-height: 1;

  @-moz-document url-prefix() {
    padding: 20px;
  }

  &::placeholder {
    transition: all 0.75s;
    font-size: 18px;
    line-height: 2.33;
    font-weight: ${themeGet('fontWeights.normal')};
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    overflow: visible;
  }
  @media only screen and (max-width: ${themeGet('breakpoints.md')}) {
    width: calc(100% - 40px);
  }

`;

export const StyledPredictiveSearchContainer = styled(PredictiveSearchContainer)`
  top: 0;
  right: 0;
  max-width: 600px;
`;

export const SearchResultsWrapper = styled.div`
  max-width: 350px;
  margin: auto;
  position: relative;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
  max-width: 550px;
  }
`;
